import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Baby,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  HeartPulse,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Move,
  Plus,
  Scale,
  Shirt,
  Sparkles,
  UserRound,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/axillary-breast-removal-mumbai";

const AXILLARY_FAQS = [
  {
    question: "Will axillary breast go away on its own without treatment?",
    answer:
      "No. Accessory breast tissue does not disappear spontaneously. It fluctuates in size with hormonal changes: it may reduce after pregnancy or after menopause when estrogen levels decline, but the tissue itself remains present. Permanent removal requires surgery. Non-surgical management through compression and lifestyle modification addresses symptoms but does not eliminate the tissue.",
  },
  {
    question: "Can exercise or weight loss remove axillary breast tissue?",
    answer:
      "No. Glandular breast tissue does not respond to exercise or diet. If the axillary fullness is predominantly glandular, it will persist regardless of how much weight is lost or how much the chest and arm muscles are developed. This is one of the distinguishing features of true accessory breast tissue compared to axillary fat, which does reduce with overall fat loss.",
  },
  {
    question: "Is axillary breast tissue dangerous?",
    answer:
      "In the vast majority of cases, no. Accessory breast tissue is benign. It can develop the same benign conditions as normal breast tissue, including fibroadenoma and cysts, but malignancy in accessory breast tissue is rare. Proper evaluation is recommended for any new lump, change in size, skin change, or other concerning feature in known accessory breast tissue.",
  },
  {
    question: "Will the tissue grow back after surgical removal?",
    answer:
      "No, provided the excision is complete. Glandular tissue that has been fully removed does not regenerate. If excision is incomplete, residual tissue may remain and respond to hormonal stimulation. This is why accurate pre-operative imaging and precise surgical technique are important for a durable result.",
  },
  {
    question: "Can axillary breast tissue be removed with liposuction only?",
    answer:
      "Liposuction alone is effective for purely fatty axillary fullness confirmed on ultrasound. For patients with glandular tissue, liposuction removes the fat component but leaves the glandular tissue behind, producing an incomplete result. Most patients with true accessory breast tissue benefit from the combination approach of liposuction plus direct excision.",
  },
  {
    question: "How visible is the scar after axillary breast surgery?",
    answer:
      "The incision is placed within the natural armpit crease. It is not visible when the arm is at rest and is concealed by the arm position in most clothing and sleeveless garments. The scar fades over 6 to 12 months and is generally described by patients as barely visible once healed.",
  },
  {
    question: "Can axillary breast tissue develop during pregnancy and then be treated afterward?",
    answer:
      "Yes. Many patients first notice significant axillary breast tissue during pregnancy when it enlarges with hormonal stimulation. Surgery is avoided during pregnancy. After delivery and once breastfeeding is complete and hormone levels have normalized, the tissue can be assessed and treated surgically. Patients who plan future pregnancies are counseled that pregnancy after surgery may cause residual normal chest breast tissue to change, though the removed accessory tissue does not return.",
  },
  {
    question: "Is the procedure performed under local or general anesthesia?",
    answer:
      "Either is appropriate depending on the volume of tissue and patient preference. Small volumes of predominantly fatty axillary tissue can be treated under local anesthesia with minimal sedation. Larger volumes or patients who prefer to be asleep for the procedure are treated under general anesthesia. Dr. Jain discusses the appropriate anesthesia option during consultation.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Axillary breast",
      alternateName: ["Accessory breast tissue", "Accessory mammary tissue", "Polymastia of the axilla"],
      description:
        "Accessory glandular breast tissue in the axilla from incomplete regression of the embryonic milk line; hormonally responsive; diagnosed clinically and with ultrasound; distinguished from axillary fat; managed with observation, liposuction for fat-predominant cases, direct excision for glandular tissue, or combined liposuction and excision.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Axilla" },
      possibleTreatment: [
        "Observation with monitoring",
        "Liposuction for predominantly fatty axillary fullness",
        "Surgical excision of glandular accessory tissue",
        "Combined liposuction and direct excision",
      ],
      signOrSymptom: [
        { "@type": "MedicalSignOrSymptom", name: "Axillary swelling" },
        { "@type": "MedicalSignOrSymptom", name: "Cyclical breast-related tenderness in the underarm" },
      ],
      url: PAGE_URL,
    },
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Axillary accessory breast tissue excision",
      description:
        "Removal of accessory breast tissue in the axilla via armpit crease incision, often with liposuction for mixed fat-gland composition; histology of specimen; local or general anesthesia.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Axilla" },
      howPerformed:
        "Marking with arm positioning; axillary crease incision; liposuction when indicated; direct excision of glandular tissue; cosmetic closure; compression garment; 1–2 hours; typically same-day discharge.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: AXILLARY_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function AxillaryJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Axillary Breast Removal", href: "/axillary-breast-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Reconstructive Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Axillary Breast Removal
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Accessory Breast Tissue Treatment by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Axillary Breast" icon="🔬" size="hero"  imageSrc={HERO.axillaryBreast} imageAlt="Axillary breast tissue"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Axillary Breast Tissue?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Axillary breast, medically called accessory breast tissue, is breast glandular tissue that develops in the underarm
              area rather than in the normal breast position on the chest. It is not abnormal growth and it is not cancer. It is
              breast tissue that developed in the wrong location during embryological development.
            </p>
            <p>
              During fetal development, a structure called the milk line runs from the armpit to the groin on each side of the body.
              Normally this line regresses completely except in the chest area where the breasts form. When a portion of this line
              fails to regress, the remaining tissue develops wherever it stopped, most commonly in the axilla. This tissue behaves
              exactly like normal breast tissue because it is breast tissue. It responds to the same hormonal signals, which is why it
              swells during menstruation, enlarges during pregnancy and breastfeeding, and reduces afterward.
            </p>
            <p>
              Dr. Vicky Jain treats axillary breast through surgical excision, liposuction, or a combination of both
              depending on the composition of the tissue. The condition does not resolve without treatment. Non-surgical approaches
              manage symptoms but do not remove the tissue. Surgery is the only permanent solution.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Axillary Breast Treatment
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first step in planning treatment is determining the composition of the axillary tissue. Axillary fullness in the
                underarm area can be caused by fat alone, glandular breast tissue alone, or a combination of both. These require
                different surgical approaches. Fat-only axillary fullness is treated effectively with liposuction. True accessory
                glandular tissue requires excision because liposuction does not reliably remove glandular tissue. Most patients have
                a combination of both, which is why the combination approach of liposuction followed by direct excision of the
                glandular component is the most commonly performed technique.
              </p>
              <p>
                Ultrasound before surgery confirms the presence and extent of glandular tissue and guides surgical planning. Dr. Jain
                assesses the axillary area clinically and with imaging before recommending the appropriate technique. The incision is
                placed within the natural armpit crease where it is concealed by the arm at rest and not visible in normal clothing or
                sleeveless garments.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SignsAndCausesSection() {
  const cards = [
    {
      title: "Soft Swelling in the Underarm",
      icon: UserRound,
      copy: "The most common presentation. A soft to firm swelling in the armpit that may be barely visible at rest but becomes prominent with certain arm positions or when wearing sleeveless clothing.",
    },
    {
      title: "Cyclical Pain or Tenderness",
      icon: HeartPulse,
      copy: "Because accessory breast tissue responds to the same hormonal signals as normal breast tissue, it frequently becomes tender or painful in the days before menstruation, then subsides. This cyclical pattern is one of the most reliable clinical indicators that the tissue is glandular rather than simple fat.",
    },
    {
      title: "Enlargement During Pregnancy",
      icon: Baby,
      copy: "Accessory breast tissue often becomes significantly more noticeable during pregnancy and breastfeeding due to elevated estrogen and progesterone levels. Some patients first notice the tissue during pregnancy when it enlarges enough to become visible and uncomfortable.",
    },
    {
      title: "Visible Bulge in Sleeveless Clothing",
      icon: Shirt,
      copy: "The axillary swelling creates a visible bulge at the armpit that is noticeable in sleeveless tops, fitted clothing, and swimwear. This cosmetic concern is the primary reason most patients seek treatment.",
    },
    {
      title: "Discomfort with Arm Movement",
      icon: Move,
      copy: "Larger volumes of accessory tissue can cause friction and discomfort during arm movement, exercise, and when wearing bras or tight clothing. The discomfort worsens when the tissue is hormonally enlarged.",
    },
    {
      title: "Persistence After Weight Loss",
      icon: Scale,
      copy: "Patients who have lost weight and found that underarm fullness persists despite overall fat reduction likely have glandular axillary breast tissue rather than fat. Glandular tissue does not reduce with weight loss or exercise.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="signs-causes-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Symptoms and Causes of Axillary Breast</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_FAT_ROWS = [
  {
    label: "Composition",
    breast: "Glandular breast tissue, with or without fat.",
    fat: "Subcutaneous fat only.",
  },
  {
    label: "Response to Hormones",
    breast: "Swells before menstruation, enlarges during pregnancy and breastfeeding.",
    fat: "Does not change with hormonal cycles.",
  },
  {
    label: "Pain",
    breast: "Often tender or painful cyclically before periods.",
    fat: "Usually painless.",
  },
  {
    label: "Response to Weight Loss",
    breast: "Does not reduce. Glandular tissue is not affected by diet or exercise.",
    fat: "Reduces with overall body fat loss.",
  },
  {
    label: "Treatment",
    breast: "Requires surgical excision of glandular tissue. Liposuction alone is insufficient for glandular tissue.",
    fat: "Liposuction alone is effective.",
  },
  {
    label: "Diagnosis",
    breast: "Ultrasound confirms glandular tissue.",
    fat: "Ultrasound shows fat only.",
  },
];

function AxillaryVsFatSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="axillary-vs-fat-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Axillary Breast vs. Axillary Fat: An Important Distinction
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[16%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Axillary Breast
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Axillary Fat
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_FAT_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.breast}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.fat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed">
          This distinction matters because liposuction alone on a patient with true glandular axillary breast tissue produces an
          incomplete result. The fat component reduces but the glandular tissue remains. Correct diagnosis before surgery prevents the
          need for revision.
        </p>
      </div>
    </section>
  );
}

function DiagnosisSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="diagnosis-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Axillary Breast is Diagnosed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Clinical examination is the starting point. Dr. Jain assesses the consistency, size, and cyclical behavior of the
              axillary swelling. Glandular tissue has a characteristic firm-to-soft texture distinct from pure fat. Cyclical tenderness
              and hormonal enlargement in the history strongly suggest accessory breast tissue.
            </p>
            <p>
              Ultrasound confirms the diagnosis and determines the proportion of glandular versus fatty tissue in the swelling. This
              directly informs the surgical plan. Mammography is added for patients over 35 or with risk factors. MRI is used in
              complex cases where ultrasound findings are inconclusive. Biopsy is arranged if any suspicious features are identified
              on imaging, though the vast majority of axillary breast tissue is benign.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Observation",
    body:
      "Appropriate for patients with minimal symptoms, small volume of tissue, and no significant cosmetic concern. Regular monitoring is recommended as accessory breast tissue carries a very small risk of the same conditions that affect normal breast tissue, including fibroadenoma, cysts, and in rare cases malignancy. Observation does not reduce the tissue but monitors it for any changes that would change the management plan.",
  },
  {
    title: "Liposuction Alone",
    body:
      "Removes the fatty component of axillary fullness through small access points in the armpit crease. Effective for patients with predominantly fatty axillary fullness confirmed on ultrasound. Not sufficient for patients with true glandular tissue, as liposuction does not reliably remove glandular breast tissue.",
  },
  {
    title: "Surgical Excision",
    body:
      "Direct removal of the glandular tissue through an incision in the axillary crease. The most definitive treatment for true accessory breast tissue. The specimen is sent for histological analysis. Appropriate when liposuction alone would be insufficient based on the glandular component identified on imaging.",
  },
  {
    title: "Combination Approach",
    body:
      "Liposuction followed by direct excision of the glandular component through the same access in the armpit crease. The most commonly performed technique for patients with mixed fat and glandular tissue. Liposuction first reduces the surrounding fat and improves access to the glandular tissue. Excision then removes the glandular component completely. Produces the best contour result for mixed composition axillary breast.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Treatment Options for Axillary Breast</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Axillary Breast Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1 to 2 hours under local or general anesthesia depending on the volume of tissue and the patient&apos;s
              preference. Dr. Jain marks the axillary area before surgery with the arm in different positions to confirm the extent of
              the tissue to be removed.
            </p>
            <p>
              For the combination approach, a small incision is made within the natural armpit crease. Liposuction is performed first to
              remove the fatty component and improve access. The glandular tissue is then directly excised through the same incision.
              The specimen is sent for histological analysis. The incision is closed with cosmetic sutures.
            </p>
            <p>
              A compression garment covering the axillary area is applied immediately after surgery. Most patients go home the same
              day. The compression garment significantly reduces swelling and supports the contour result during healing. It is worn
              continuously for the first week and during the day for the following 2 to 3 weeks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and discomfort in the axillary area. The compression garment stays on continuously. Normal and expected. Arm movement is slightly restricted by swelling rather than by the surgery itself.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Most patients resume normal daily activity including desk work. The axillary swelling that was present before surgery is gone. Some post-operative swelling from the procedure itself remains.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Compression garment worn during the day. Return to normal activity. The axillary contour improves as post-operative swelling reduces.",
      icon: Clock,
    },
    {
      period: "Weeks 4 to 6",
      description:
        "Final contour is visible. The axillary area is smooth and the incision scar sits within the armpit crease. Strenuous arm exercise resumes.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Axillary Breast Removal?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are patients with confirmed accessory breast tissue causing cosmetic concern, physical discomfort,
              cyclical pain, or restriction in clothing choices. There is no specific age requirement. The condition is present from
              birth but often becomes noticeable at puberty, worsens during pregnancy, and persists thereafter. Patients who notice
              cyclical tenderness or enlargement with hormonal changes have the strongest indicator of glandular tissue and the
              clearest indication for surgical excision rather than liposuction alone.
            </p>
            <p>
              Patients who have tried weight loss and found that axillary fullness persists are appropriate candidates as this pattern
              indicates glandular rather than fatty tissue. Patients in good general health without active infection or uncontrolled
              systemic conditions are suitable for surgery. Those who want a permanent result rather than temporary symptom management
              through lifestyle modification or compression are appropriate for surgical treatment. Dr. Jain advises patients who are
              planning pregnancy to consider timing, as pregnancy will cause the residual normal breast tissue in the chest to change
              and any unremoved accessory tissue to enlarge again if the excision was incomplete.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {AXILLARY_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`axillary-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Axillary Breast Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the composition and extent of your axillary tissue, reviews imaging, and
          determines whether liposuction, excision, or a combination approach is appropriate for your anatomy. You leave with a clear
          diagnosis, a surgical plan, and a realistic picture of the result and recovery.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Augmentation",
      description: "Breast enhancement for patients addressing multiple breast concerns alongside axillary tissue removal.",
      href: "/breast-implant-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Body contouring to complement underarm correction and overall silhouette improvement.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Gynecomastia Surgery",
      description: "For male patients with both axillary tissue and chest glandular excess.",
      href: "/gynecomastia-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AxillaryBreastRemovalPage() {
  return (
    <>
      <SEO
        title="Axillary Breast Removal | Dr. Vicky Jain"
        description="Underarm swelling that worsens with hormones or pregnancy is likely accessory breast tissue. Dr. Vicky Jain removes axillary breast. Book your consultation."
        canonical="/axillary-breast-removal-mumbai"
      />
      <AxillaryJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <SignsAndCausesSection />
      <AxillaryVsFatSection />
      <DiagnosisSection />
      <TreatmentOptionsSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
